/**
 * useMeasuredHeight — the live height of an element, from the moment it exists.
 *
 * WHY A CALLBACK REF. The obvious version — a `useRef` plus a mount-time
 * `useEffect` that observes `ref.current` — silently measures nothing whenever
 * the element is not in the DOM on that first pass. The gym shell is exactly
 * that case: it renders a loading screen while auth resolves, so the pinned
 * header does not exist yet when App mounts, the observer is never attached,
 * and the height stays 0 forever. That went unnoticed for as long as the value
 * was only advisory. It stopped being advisory the moment the pinned block went
 * `position: fixed` and this height became the spacer holding its place: a 0
 * spacer puts the top of every page underneath the header.
 *
 * A callback ref runs whenever the node appears, changes or goes away, so there
 * is no first pass to miss. It also takes a reading immediately rather than
 * waiting for the observer's first callback, so the first painted frame already
 * has the right height and nothing jumps.
 *
 * Usage:
 *
 *   const [headerRef, headerHeight] = useMeasuredHeight();
 *   <div ref={headerRef} style={{ position: "fixed", ... }}>…</div>
 *   <div aria-hidden="true" style={{ height: headerHeight }} />
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** @returns {[function, number]} a ref callback, and the element's border-box height */
export function useMeasuredHeight() {
  const [height, setHeight] = useState(0);
  const observer = useRef(null);

  const ref = useCallback(node => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) { setHeight(0); return; }
    // Read once right here: a ref callback runs in the commit, so this lands
    // before the browser paints. Waiting for the observer would paint one frame
    // with the old height.
    setHeight(node.offsetHeight || 0);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => {
      setHeight(e.borderBoxSize?.[0]?.blockSize ?? e.target.offsetHeight ?? 0);
    });
    ro.observe(node);
    observer.current = ro;
  }, []);

  useEffect(() => () => { observer.current?.disconnect(); observer.current = null; }, []);

  return [ref, height];
}
